import {
  AutoRedirect,
  Button,
  FLEET_OVERVIEW_URL,
  getGroupingDistanceForZoom,
  groupSitesByDistance,
  INITIAL_ZOOM,
  PageTitle,
  ROOT_EQUIPMENT_STATUS_MAP,
  RootEquipmentData,
  SiteData,
  SiteGroup,
  Spinner,
  useGetFlatHierarchy,
  useSelectedSites,
} from "@abb-connect/react-shared";
import { useRootEquipmentStatusApi } from "@api/telemetry-api";
import { useIsAbbUserApi, useIsUserAdminApi } from "@api/users-api";
import { useMsal } from "@azure/msal-react";
import SitePopup from "@components/SitePopup/SitePopup";
import {
  assignStatusToHierarchy,
  countByStatus,
} from "@services/status-service";
import type { Map as LeafletMap } from "leaflet";
import { useCallback, useMemo, useState } from "react";
import { MapContainer, TileLayer, useMapEvents } from "react-leaflet";
import { getUrlToRedirectTo } from "./helper";
import styles from "./HomePage.module.css";

//TODO: Enable when fleet overview is working again
const FLEET_OVERVIEW_ENABLED = false;

function ZoomTracker({
  onZoomChange,
}: {
  onZoomChange: (zoom: number) => void;
}) {
  useMapEvents({
    zoomend: (e) => {
      onZoomChange((e.target as LeafletMap).getZoom());
    },
  });
  return null;
}

function HomePage() {
  const { accounts } = useMsal();
  const account = accounts[0] || null;

  const { selectedSites } = useSelectedSites();

  const { data: isAbbUser } = useIsAbbUserApi();
  const { data: isUserAdmin } = useIsUserAdminApi();

  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const handleZoomChange = useCallback(
    (newZoom: number) => setZoom(newZoom),
    []
  );

  const { data: flatHierarchy, isLoading: isHierarchyLoading } =
    useGetFlatHierarchy();

  const hierarchy = useMemo(() => {
    if (!flatHierarchy) return undefined;
    const maxDistance = getGroupingDistanceForZoom(zoom);
    return groupSitesByDistance(flatHierarchy, maxDistance);
  }, [flatHierarchy, zoom]);

  const queries = useRootEquipmentStatusApi(
    flatHierarchy ? flatHierarchy.map((p) => p.name) : []
  );

  const markers = useMemo(() => {
    const data = queries
      .filter((query) => query.isSuccess && query.data != null)
      .flatMap((query) => query.data);
    return assignStatusToHierarchy(hierarchy, data);
  }, [hierarchy, queries]);

  const visibleMarkers = useMemo(() => {
    if (selectedSites.length === 0) return markers;
    return markers.filter((group: SiteGroup) =>
      group.plants.some((p: SiteData) => selectedSites.includes(p.name))
    );
  }, [markers, selectedSites]);

  const statuses = countByStatus(
    visibleMarkers.flatMap((m: SiteGroup) =>
      m.plants.flatMap((p: SiteData) =>
        p.rootEquipment.map((re: RootEquipmentData) => re.status)
      )
    )
  );

  if (isHierarchyLoading) {
    return (
      <div className={styles["loading-container"]}>
        <Spinner />
      </div>
    );
  }

  const redirectUrl = getUrlToRedirectTo(hierarchy) ?? "";

  return (
    <AutoRedirect to={redirectUrl} shouldRedirect={redirectUrl !== ""}>
      <div className={styles["home-page"]}>
        <PageTitle
          title={`Welcome, ${account.name?.split(" ")[0]}`}
          subtitle="Please select a site"
        />
        <div className={styles["map-container"]}>
          <div className={styles["map-header"]}>
            <div className={styles["status-header"]}>
              {statuses.slice(1).map((s, i) => {
                const status = ROOT_EQUIPMENT_STATUS_MAP.get(i)!;
                return (
                  <span key={status.label} className={styles.status}>
                    <div
                      className={styles.circle}
                      style={{
                        backgroundColor: status.color,
                      }}
                    />
                    <p>
                      [{s}] {status.label}
                    </p>
                  </span>
                );
              })}
            </div>
            {FLEET_OVERVIEW_ENABLED && isAbbUser && isUserAdmin && (
              <Button size="small" variant="secondary">
                <a href={FLEET_OVERVIEW_URL}>Fleet Overview</a>
              </Button>
            )}
          </div>
          <MapContainer
            center={[45, 0]}
            zoom={INITIAL_ZOOM}
            minZoom={2}
            scrollWheelZoom={true}
            className={styles.map}
            attributionControl={false}
            maxBounds={[
              [-85, -Infinity],
              [85, Infinity],
            ]}
            maxBoundsViscosity={1.0}
            worldCopyJump={true}
          >
            <ZoomTracker onZoomChange={handleZoomChange} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              noWrap={false}
              className={styles["tile-layer"]}
            />
            {visibleMarkers?.map((m) => (
              <SitePopup plantGroup={m} key={m.plants[0].name} />
            ))}
          </MapContainer>
        </div>
      </div>
    </AutoRedirect>
  );
}

export default HomePage;
